import { useCallback, useState } from "react";
import { oktaConfig } from "../config/okta";
import { isAuthenticated, logout } from "../services/authService";
import { randomString, sha256Base64Url } from "../utils/pkce";

export const LoginPage = () => {
  const [isRedirecting, setIsRedirecting] = useState(false);
  const authenticated = isAuthenticated();

  const handleLogin = useCallback(async () => {
    const { issuer, clientId, redirectUri, scopes } = oktaConfig;
    if (!issuer || !clientId) {
      alert("Okta is not configured. Set VITE_OKTA_ISSUER and VITE_OKTA_CLIENT_ID in .env");
      return;
    }

    setIsRedirecting(true);

    const codeVerifier = randomString(64);
    const codeChallenge = await sha256Base64Url(codeVerifier);
    const state = randomString(32);

    sessionStorage.setItem("pkce_code_verifier", codeVerifier);
    sessionStorage.setItem("pkce_state", state);

    const params = new URLSearchParams({
      client_id: clientId,
      response_type: "code",
      redirect_uri: redirectUri,
      scope: scopes,
      state,
      code_challenge: codeChallenge,
      code_challenge_method: "S256",
    });

    window.location.href = `${issuer}/v1/authorize?${params.toString()}`;
  }, []);

  return (
    <div className="flex min-h-screen items-center justify-center bg-light-bg font-sans">
      <div className="w-full max-w-md px-6">
        <div className="rounded-2xl border border-gray-200 bg-white px-8 py-10 shadow-card">
          {/* Logo */}
          <div className="mb-8 flex justify-center">
            <img
              src="/assets/tokio-marine-logo.png"
              alt="Tokio Marine HCC"
              className="h-12 w-auto"
            />
          </div>

          {/* Title */}
          <h1 className="mb-2 text-center text-2xl font-bold text-heading">
            Welcome to ContractIQ
          </h1>
          <p className="mb-8 text-center text-sm text-body/70">
            Sign in with your organization account to continue.
          </p>

          {/* Login button */}
          <button
            type="button"
            onClick={() => void handleLogin()}
            disabled={isRedirecting}
            className="flex w-full items-center justify-center gap-2.5 rounded-xl bg-primary-500 px-4 py-3 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {isRedirecting ? (
              <>
                <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                Redirecting to Okta…
              </>
            ) : (
              <>
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 9V5.25A2.25 2.25 0 0013.5 3h-6a2.25 2.25 0 00-2.25 2.25v13.5A2.25 2.25 0 007.5 21h6a2.25 2.25 0 002.25-2.25V15m3-3l3-3m0 0l-3-3m3 3H9" />
                </svg>
                Sign in with Okta
              </>
            )}
          </button>

          {authenticated && (
            <button
              type="button"
              onClick={() => void logout()}
              className="mt-3 w-full rounded-xl border border-gray-300 px-4 py-2 text-sm font-medium text-heading transition-colors hover:bg-gray-50"
            >
              Logout and clear session
            </button>
          )}

          {/* Divider */}
          <div className="my-6 flex items-center gap-3">
            <div className="h-px flex-1 bg-divider" />
            <span className="text-xs font-medium text-body/40">SECURED BY OKTA</span>
            <div className="h-px flex-1 bg-divider" />
          </div>

          {/* Footer note */}
          <p className="text-center text-xs text-body/50 leading-relaxed">
            By signing in, you agree to your organization&apos;s security policies.
            Contact your administrator if you&apos;re unable to sign in.
          </p>
        </div>

        {/* Bottom brand line */}
        <p className="mt-6 text-center text-xs text-body/30">
          Tokio Marine HCC &mdash; ContractIQ Platform
        </p>
      </div>
    </div>
  );
};
